import * as React from 'react'
import { useState } from 'react'

import { Save } from 'lucide-react'
import { LngLat, LngLatBounds } from 'mapbox-gl'
import { inject, observer } from 'mobx-react'

import { IProjectAddressInput } from '~/client/graph/types.generated'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { mapboxFeatureToAddress } from '~/client/src/shared/components/MapBoxViewer/mapboxAddress'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { IMapBoxFeature } from '~/client/src/shared/interfaces/IMapBoxFeature'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  areProjectAddressesEqual,
  getAddressCopy,
  toAddressBoundsInputFromMapBox,
  toLngLat,
  toLngLatBounds,
} from '~/client/src/shared/utils/Address'
import { NOOP } from '~/client/src/shared/utils/noop'

import ProjectSetUpPageStore from '../../../ProjectSetUpPage.store'
import {
  areLngLatBoundsEqual,
  areLngLatEqual,
  MapBoxView,
} from '../components/MapBoxView'
import ProjectSettingsStore from '../ProjectSettings.store'
import ProjectHeaderImageBlock from './ProjectHeaderImageBlock'
import ProjectInputBlock from './ProjectInputBlock'
import ProjectLocationBlock from './ProjectLocationBlock'
import ProjectLogoBlock from './ProjectLogoBlock'
import ProjectSelectorBlock from './ProjectSelectorBlock'
import ProjectUserTagsBlock from './ProjectUserTagsBlock'

interface IProps {
  store: ProjectSettingsStore
  mapBoxOnQueryChange: (query: string) => Promise<void>
  isProjectCreationAvailable: boolean
  projectSetUpPageStore: ProjectSetUpPageStore
}

interface InjectedProps extends IProps {
  eventsStore?: DesktopEventStore
}

const ProjectDetails: React.FC<InjectedProps> = observer(
  ({
    store,
    mapBoxOnQueryChange,
    isProjectCreationAvailable,
    projectSetUpPageStore,
    eventsStore,
  }: InjectedProps) => {
    const [isProjectLogoChanged, setIsProjectLogoChanged] = useState(false)
    const [isProjectHeaderImageChanged, setIsProjectHeaderImageChanged] =
      useState(false)

    const {
      projectAddress,
      projectName,
      projectCode,
      isProjectErrorMessageVisible,
    } = store

    if (!projectAddress) {
      return null
    }

    const mapBoxOnAddressValueChanged = (item: IMapBoxFeature) => {
      const addr = mapboxFeatureToAddress(item)
      store.setProjectAddress(addr)
    }

    const onSaveAddress = () => {
      store.saveProjectAddress()
    }

    const isAdressButtonDisabled = areProjectAddressesEqual(
      projectAddress,
      eventsStore?.appState.projectAddress,
    )

    const onProjectNameChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      store.setProjectName(event.target.value)
    }

    const onProjectLogoChange = (logoUrl: string) => {
      setIsProjectLogoChanged(true)
      store.setProjectLogo(logoUrl)
    }

    const onProjectNameSave = () => {
      store.saveProjectName()
    }

    const onProjectLogoSave = () => {
      store.saveProjectLogo()
    }

    const onProjectHeaderImageChange = (headerImageUrl: string) => {
      setIsProjectHeaderImageChanged(true)
      store.setProjectHeaderImage(headerImageUrl)
    }

    const onProjectHeaderImageSave = () => {
      store.saveProjectHeaderImage()
    }

    const onMapBoxDataChanged = (
      bearing: number,
      bounds: LngLatBounds,
      center: LngLat,
      zoom: number,
      pitch: number,
    ) => {
      const addr = getAddressCopy(projectAddress)
      let changed = false

      if (addr.bearing !== bearing) {
        changed = true
        addr.bearing = bearing
      }

      if (!areLngLatBoundsEqual(toLngLatBounds(addr.bounds), bounds)) {
        changed = true
        addr.bounds = toAddressBoundsInputFromMapBox(bounds)
      }

      if (!areLngLatEqual(toLngLat(addr.center), center) || !addr.center) {
        changed = true
        addr.center = center
      }

      if (zoom !== addr.zoom) {
        changed = true
        addr.zoom = zoom
      }

      if (pitch !== addr.pitch) {
        changed = true
        addr.pitch = pitch
      }

      if (changed) {
        store.setProjectAddress(addr)
      }
    }

    const onAddressChanged = (addr: IProjectAddressInput) => {
      if (addr !== projectAddress) {
        store.setProjectAddress(addr)
        store.saveProjectAddress()
      }
    }

    const isProjectNameButtonDisabled = projectName === store.activeProject.name

    const renderFormSaveButton = (
      clickHandler: () => void,
      isHidden?: boolean,
      isDisabled?: boolean,
    ) => (
      <Button
        type="button"
        className={isHidden ? 'tw:invisible tw:self-end' : 'tw:self-end'}
        disabled={isDisabled}
        onClick={clickHandler}
      >
        <Save data-icon="inline-start" />
        {Localization.translator.save}
      </Button>
    )

    return (
      <div className="tw:mr-10 tw:flex tw:h-full tw:flex-row">
        <div className="tw:flex tw:flex-col">
          <ProjectSelectorBlock
            projects={store.projects}
            activeProject={projectSetUpPageStore.state.activeProject}
            isProjectCreationAvailable={isProjectCreationAvailable}
            selectProject={store.selectProject}
            onNewProjectSelected={store.onNewProjectSelected}
          />
          <ProjectInputBlock
            id="project-details-project-name"
            text={projectName}
            errorMessageText={
              Localization.translator.projectDetailsDescriptions
                .projectNameError
            }
            label={Localization.translator.projectName}
            onChange={onProjectNameChange}
            onButtonClick={onProjectNameSave}
            isHidden={isProjectNameButtonDisabled}
            isErrorVisible={isProjectErrorMessageVisible}
            isNewProjectSelected={store.isNewProjectSelected}
            renderDoneButton={renderFormSaveButton}
          />
          <ProjectInputBlock
            id="project-details-project-code"
            text={projectCode}
            errorMessageText={
              Localization.translator.projectDetailsDescriptions
                .projectCodeError
            }
            label={Localization.translator.projectCode}
            onChange={NOOP}
            onButtonClick={NOOP}
            isHidden={true}
            isErrorVisible={false}
            isReadOnly={true}
            isNewProjectSelected={store.isNewProjectSelected}
            renderDoneButton={renderFormSaveButton}
          />
          <ProjectHeaderImageBlock
            projectHeaderImageUrl={store.projectHeaderImageUrl}
            projectId={projectSetUpPageStore.state.activeProject.id}
            isNewProjectSelected={store.isNewProjectSelected}
            isProjectHeaderImageChanged={isProjectHeaderImageChanged}
            onProjectHeaderImageChange={onProjectHeaderImageChange}
            onProjectHeaderImageSave={onProjectHeaderImageSave}
            renderDoneButton={renderFormSaveButton}
          />
          <ProjectLogoBlock
            projectLogoUrl={store.projectLogoUrl}
            projectId={projectSetUpPageStore.state.activeProject.id}
            isNewProjectSelected={store.isNewProjectSelected}
            isProjectLogoChanged={isProjectLogoChanged}
            onProjectLogoChange={onProjectLogoChange}
            onProjectLogoSave={onProjectLogoSave}
            renderDoneButton={renderFormSaveButton}
          />
          <ProjectLocationBlock
            projectAddress={projectAddress}
            projectAddressSuggestions={store.projectAddressSuggestions}
            isNewProjectSelected={store.isNewProjectSelected}
            isAdressButtonDisabled={isAdressButtonDisabled}
            mapBoxOnAddressValueChanged={mapBoxOnAddressValueChanged}
            mapBoxOnQueryChange={mapBoxOnQueryChange}
            onSaveAddress={onSaveAddress}
            renderDoneButton={renderFormSaveButton}
          />
          {!store.isNewProjectSelected && <ProjectUserTagsBlock />}
        </div>
        <MapBoxView
          className="tw:flex-1 tw:max-w-[800px] tw:max-h-[600px]"
          bearing={projectAddress.bearing}
          center={toLngLat(projectAddress.center)}
          onAddressChanged={onAddressChanged}
          onDataChanged={onMapBoxDataChanged}
          zoom={projectAddress.zoom}
        />
      </div>
    )
  },
)

export default inject('eventsStore')(ProjectDetails)
